import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LanguageContext = createContext();

// La langue est portée par l'URL : /cpo en français, /en/cpo en anglais.
// Google peut ainsi indexer les deux versions séparément.
export const getLanguageFromPath = (pathname) =>
  pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'fr';

export const stripLanguagePrefix = (pathname) => {
  if (getLanguageFromPath(pathname) !== 'en') return pathname;
  return pathname.slice('/en'.length) || '/';
};

export const localizePath = (path, language) => {
  if (language !== 'en') return path;
  return path === '/' ? '/en' : `/en${path}`;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// navigate() qui garde la langue courante : navigate('/cpo') mène à /en/cpo sur le site anglais.
export const useLocalizedNavigate = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  return useCallback((path, options) => navigate(localizePath(path, language), options), [navigate, language]);
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const language = getLanguageFromPath(location.pathname);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((nextLanguage) => {
    if (nextLanguage === language) return;
    const basePath = stripLanguagePrefix(location.pathname);
    navigate(localizePath(basePath, nextLanguage) + location.hash);
  }, [language, location.pathname, location.hash, navigate]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    toggleLanguage: () => setLanguage(language === 'fr' ? 'en' : 'fr'),
    switchToFrench: () => setLanguage('fr'),
    switchToEnglish: () => setLanguage('en'),
    localizePath: (path) => localizePath(path, language),
    isFrench: language === 'fr',
    isEnglish: language === 'en'
  }), [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
